import { type NavigationTreeDataDTO } from "~/features/notifications/api/getNavigationTree";
import { Box, Typography, styled } from "~/common/mui";
import { OnOffSwitch } from "~/features/notifications/NotificationSettingsPage/OnOffSwitch";
import { NotificationSettingsConfirmationPopup } from "~/features/notifications/NotificationSettingsPage/NotificationSettingsConfirmationPopup";
import { requestConfirmation } from "~/common/utils/ModalService";
import { useTranslation } from "react-i18next";

const CategoryContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
}));

const CategoryCell = styled(Box)({
  padding: "24px 16px",
  display: "flex",
  justifyContent: "space-between",
});

const ItemCell = styled(Box)(({ theme }) => ({
  padding: "16px 16px 16px 32px",
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  justifyContent: "space-between",
}));

interface Props {
  onToggle: (isParent: boolean, type: string, newValue: boolean) => void;
  data: NavigationTreeDataDTO[];
}
export const NotificationSettings = ({ onToggle, data }: Props) => {
  const { t } = useTranslation();

  const onToggleParent = async (newVal: boolean, parentType: string) => {
    if (parentType === "ALL" && !newVal) {
      const confirmed = await requestConfirmation((props) => <NotificationSettingsConfirmationPopup {...props} />);
      if (!confirmed) {
        return;
      }
    }
    onToggle(true, parentType, newVal);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {data.map((item, i) => (
        <CategoryContainer key={item.parentType} id={item.parentType}>
          <CategoryCell>
            <Typography variant="titleMedium" sx={{ fontWeight: "bold" }}>
              {t(`${item.parentTypeTranslation}`)}
            </Typography>
            <OnOffSwitch
              selected={item.enabledParent}
              onToggle={(newVal) => onToggleParent(newVal, item.parentType)}
              switchTestId={`toggle-notification-${i}`}
            />
          </CategoryCell>
          {item.childTypes?.map((childItem) => (
            <ItemCell key={childItem.type}>
              <Typography variant="titleMedium">{t(childItem.typeTranslation)}</Typography>
              <OnOffSwitch
                selected={childItem.enabled}
                onToggle={(newVal) => onToggle(false, childItem.type, newVal)}
                switchTestId={`toggle-notification-${i}`}
              />
            </ItemCell>
          ))}
        </CategoryContainer>
      ))}
    </Box>
  );
};
